import * as React from 'react';
import {useSelector} from 'react-redux';

import {Markets} from 'nutshell-core/markets';
import type {ProductId, Market} from 'nutshell-core/types';

import type {ProductPickerProduct} from '../../../../../ui/core/select/graphql/helpers/product-picker-product';
import {ReduxSelectProducts} from '../../../../../ui/core/select/redux-select-products';
import type {ProductOption} from '../../../../../ui/core/select/redux-select-products';

import {CancelButton} from '../button/cancel-button/cancel-button';
import {TextField} from '../textfield/textfield';

import styles from './product-picker.module.css';

type Props = {
    input: {
        value: any | null | undefined;
        onChange: (productMap: any) => any;
    };
    selectedOptions: any[];
    onRemove: () => any;
    currentMarket: Market | null | undefined;
};

export function ReduxFormProductPicker(props: Props) {
    const {input} = props;
    const defaultMarket = useSelector((state) => Markets.getDefaultMarket(state));
    const currentMarket = props.currentMarket || defaultMarket;
    const currentMarketId = currentMarket?.id;

    const [productId, setProductId] = React.useState<ProductId | null | undefined>(
        (input.value && input.value.links && input.value.links.product) || null
    );
    const [selectedProduct, setSelectedProduct] = React.useState<ProductPickerProduct | null>(null);
    const [quantity, setQuantity] = React.useState<string>(
        (input.value && input.value.quantity) || ''
    );
    const initialPrice = input.value && input.value.price;
    const [price, setPrice] = React.useState<string>(
        initialPrice !== null && typeof initialPrice === 'object'
            ? String(initialPrice.amount ?? '')
            : initialPrice || ''
    );
    const previousMarketId = React.useRef(currentMarketId);

    const getCurrencySymbol = () => {
        return currentMarket ? currentMarket.currencySymbol : '';
    };

    // if market changes, update the price to reflect the new market
    React.useEffect(() => {
        if (!currentMarketId) {
            previousMarketId.current = currentMarketId;
            return;
        }

        if (
            previousMarketId.current === currentMarketId ||
            !selectedProduct ||
            selectedProduct.id !== productId ||
            selectedProduct.priceMarketId !== currentMarketId
        ) {
            return;
        }

        previousMarketId.current = currentMarketId;
        const marketPrice = selectedProduct.prices[currentMarketId];
        const newPrice = marketPrice || {amount: 0};

        handleChangePrice(newPrice.amount.toString());
    }, [currentMarketId, productId, selectedProduct]);

    React.useEffect(() => {
        if (productId) {
            handleSave();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productId, quantity, price]);

    const handleClearProduct = () => {
        setProductId(null);
        setSelectedProduct(null);
        setQuantity('');
        setPrice('');

        if (input.value) {
            props.onRemove();
        }
    };

    // @ts-expect-error
    const handleKeyDownProductInputs = (event) => {
        // Capture the ENTER key to confirm the product and not submit the form.
        if (event.which === 13) {
            event.preventDefault();
            handleSave();
        }

        // Capture the ESC key to cancel the product picker.
        if (event.which === 27) {
            event.preventDefault();
            handleClearProduct();
        }
    };

    const handleSelectProduct = (value: ProductOption) => {
        const existingProductMap = typeof input.value === 'object' ? input.value : undefined;
        const product = value.product;

        let productPrice;
        if (existingProductMap) {
            productPrice = existingProductMap.price.amount;
        }

        if (currentMarket) {
            const marketPrice = product.prices[currentMarket.id];
            productPrice = marketPrice ? marketPrice.amount : '0';
        } else {
            productPrice = '0';
        }

        setSelectedProduct(product);
        setProductId(value.value);
        setPrice(productPrice || '0');
        setQuantity(existingProductMap ? existingProductMap.quantity : '1');
    };

    const handleChangeQuantity = (newQuantity: string) => {
        setQuantity(newQuantity);
    };

    const handleBlurQuantity = () => {
        if (quantity === '0') {
            handleClearProduct();
        } else if (quantity === '') {
            setQuantity('1');
        }
    };

    const handleChangePrice = (newPrice: string) => {
        setPrice(newPrice);
    };

    const handleBlurPrice = () => {
        const formattedPrice = Number(price).toLocaleString(undefined, {
            minimumFractionDigits: 2,
        });

        setPrice(formattedPrice);
    };

    const handleSave = () => {
        const existingProductMap = typeof input.value === 'object' ? input.value : undefined;

        const amount =
            price && typeof price === 'string' ? parseFloat(price.replace(/,/g, '')) : 0.0;
        const productPrice = {
            amount,
            formatted: `${getCurrencySymbol()}${price}`,
        } as const;

        // Round to 2 decimal places — backend column is DECIMAL(18,2) (ESC-360).
        const productQuantity = Math.round(Number(quantity || '1') * 100) / 100;

        if (existingProductMap) {
            const updatedProductMap = {
                ...existingProductMap,
                quantity: productQuantity,
                price: productPrice,
            } as const;

            input.onChange(updatedProductMap);
        } else {
            if (!selectedProduct || selectedProduct.id !== productId) return;

            input.onChange({
                type: 'productMaps',
                name: selectedProduct.name,
                quantity: productQuantity,
                price: productPrice,
                links: {
                    product: productId,
                },
            });
        }
    };

    // filter out already selected products from the list of available products
    const excludeProductIds = props.selectedOptions
        .filter((field) => field && field.links && field.links.product)
        .map((field) => field.links.product);
    const isSelectedProductReady =
        Boolean(productId) &&
        selectedProduct !== null &&
        selectedProduct.id === productId &&
        selectedProduct.priceMarketId === (currentMarketId ?? null);

    return (
        <div className={styles.container}>
            <div className={styles['select-container']}>
                <ReduxSelectProducts
                    selectedId={productId}
                    onChange={handleSelectProduct}
                    onSelectedProductLoad={setSelectedProduct}
                    currentMarket={currentMarket}
                    excludeProductIds={excludeProductIds}
                    placeholder='Select a product…'
                    defaultMenuIsOpen={false}
                    maxMenuHeight={200}
                    menuPortalTarget={document.body}
                />
            </div>
            <label className={styles['product-picker-label']}>
                Qty:
                <TextField
                    // @ts-expect-error
                    name='quantity'
                    disabled={!isSelectedProductReady}
                    min='0'
                    step='0.01'
                    inputMode='decimal'
                    type='number'
                    value={quantity}
                    onChange={handleChangeQuantity}
                    onBlur={handleBlurQuantity}
                    onKeyDown={handleKeyDownProductInputs}
                    selectOnFocus={true}
                />
            </label>
            <label className={styles['product-picker-label--price']}>
                Price:
                <TextField
                    // @ts-expect-error
                    disabled={!isSelectedProductReady}
                    value={price}
                    onChange={handleChangePrice}
                    onBlur={handleBlurPrice}
                    onKeyDown={handleKeyDownProductInputs}
                    selectOnFocus={true}
                />
            </label>
            <div className={styles['cancel-button']}>
                {productId && <CancelButton onClick={handleClearProduct} />}
            </div>
        </div>
    );
}
